import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Divider,
  addToast,
  Modal,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { api } from "@/lib/api-client";
import { useClientFeature } from "@/hooks/useClientFeature";
import {
  getCompanySplitFormErrors,
  getCompanySplitState,
} from "@/utils/subscription-shares";
import { getApiErrorMessage } from "@/utils/api-error";
import {
  CommitmentWarningDialog,
  isUnderCommitment,
} from "@/Components/CommitmentWarningDialog";
import { useAvailableFiscalizationTaxSystems } from "@/hooks/useAvailableFiscalizationTaxSystems";
import {
  getDefaultTaxPercentage,
  getTaxPercentageItems,
  getTaxPercentageValues,
} from "@/utils/taxPercentages";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import LimitationsSection from "@/pages/Dashboard/Subscriptions/components/tabs/LimitationsSection";
import type { LimitationTimeframe } from "@/server-types";
import { CompanySplitFields } from "./CompanySplitFields";
import {
  CustomSubscriptionServicesEditor,
  type PlanLimitation,
} from "./CustomSubscriptionServicesEditor";
import {
  buildSettingsUpdateBody,
  type SettingsFormValues,
} from "./customerSubscriptionSettingsBody";
import { getSelectableServices } from "./getSelectableServices";
import {
  type PlanValidationError,
  validatePlanLimitations,
  validatePlanServices,
} from "./validateCustomSubscriptionPlan";
import { returnCurrencySymbol } from "../../../../../../../../shared/utils/utils";
import { CustomerSubscriptionLocationsField } from "@/pages/Dashboard/Subscriptions/components/SubscriptionLocationsField";

function getPlanValidationMessage(error: PlanValidationError | null) {
  const messages: Record<PlanValidationError, string> = {
    invalidLimitAmount: t`Število obiskov mora biti celo število, večje od 0.`,
    repeatedTimeframe: t`Vsak časovni okvir je lahko omejen le enkrat.`,
    missingService: t`Izberite storitev ali odstranite prazno vrstico.`,
    duplicateService: t`Posamezna storitev je lahko dodana le enkrat.`,
  };
  return error ? messages[error] : null;
}

function toPlanLimitations(
  limitations: { timeframe: LimitationTimeframe; limitAmount: number }[],
): PlanLimitation[] {
  return limitations.map(({ timeframe, limitAmount }) => ({
    timeframe,
    limitAmount,
  }));
}

type CustomerSubscriptionSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
};

export const CustomerSubscriptionSettingsModal = ({
  isOpen,
  onClose,
  customerSubscriptionId,
}: CustomerSubscriptionSettingsModalProps) => {
  const { mutateAsync: updateSubscription, isPending: isUpdatingSubscription } =
    api.customer.useUpdateCustomerSubscription();

  const {
    data: customerSubscriptionResponse,
    isPending: isSubscriptionLoading,
  } = api.customer.useGetCustomerSubscription({
    customerSubscriptionId,
    enabled: isOpen,
  });
  const subscription = customerSubscriptionResponse?.customerSubscription;
  const commitmentLock = subscription?.commitmentLock;
  const isCommitmentLocked = isUnderCommitment(commitmentLock);

  // Held with the values the warning was raised for, so confirming saves that change even if
  // the form has moved on underneath it.
  const [pendingConfirmation, setPendingConfirmation] =
    useState<SettingsFormValues | null>(null);

  const { data: currency } = api.values.useGetCurrency({});
  const currencySymbol = currency ? returnCurrencySymbol({ currency }) : "";

  const { availableTaxSystems, hasActiveFiscalizations } =
    useAvailableFiscalizationTaxSystems();
  const { isFeatureEnabled } = useClientFeature();
  // Estimate controls need the subscriptions integration; the tax rate is editable whenever
  // the tax register is set up, so rates can be managed before the integration is enabled.
  const canIssueEstimates =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");
  const hasSubscriptionNotifications = isFeatureEnabled(
    "subscriptionNotificationSettings",
  );
  // Billing notifications go out whether or not this package issues estimates.
  const offersBillingNotifications =
    canIssueEstimates || hasSubscriptionNotifications;

  const { data: fiscalizations } =
    api.taxRegister.useGetTaxRegisterFiscalizations({
      enabled: isOpen && isFeatureEnabled("taxRegister"),
    });

  const { data: subscriptionSettings } =
    api.subscription.useGetSubscriptionSettings();

  const defaultEstimateOrganizationName =
    subscriptionSettings?.defaultOrganizationNameForSubscriptionEstimates ??
    null;
  const defaultEstimateOrganizationPlaceholder = defaultEstimateOrganizationName
    ? t`Privzeta (${defaultEstimateOrganizationName})`
    : t`Privzeta organizacija`;

  const isCustomSubscription =
    subscription != null && subscription.variation == null;

  const { data: servicesData, isFetching: isServicesLoading } =
    api.service.useGetServices({ enabled: isOpen && isCustomSubscription });

  const { data: locationsData, isFetching: isLocationsLoading } =
    api.location.useGetLocationsByClient();
  const offeredSubscription = subscription?.variation?.subscription ?? null;
  const offeredLocations = (locationsData?.locations ?? []).filter(
    (location) =>
      offeredSubscription == null ||
      offeredSubscription.allLocations ||
      offeredSubscription.locationIds.includes(location.locationId),
  );

  const now = Date.now();
  // Any payer's issued-but-unpaid estimate pins the period: a price or split change would drift from it.
  const affectedIssuedEstimatesCount =
    subscription?.invoices.filter(
      (invoice) =>
        invoice.shares.some(
          (share) =>
            share.spaceInvoicesDocumentId != null && share.paidAt == null,
        ) && new Date(invoice.periodEnd).getTime() >= now,
    ).length ?? 0;
  const hasAffectedIssuedEstimates = affectedIssuedEstimatesCount > 0;

  const form = useForm<SettingsFormValues>({
    initialValues: {
      name: "",
      isRecurring: false,
      autoRenewOnVisitExhaustion: false,
      price: 0,
      taxPercentage: 0,
      shouldIssueEstimates: true,
      shouldReceiveEstimatesOnEmail: true,
      estimateOrganizationId: null,
      companySplitEnabled: false,
      payerCompanyId: null,
      companyShareType: "percentage",
      companyShareValue: 0,
      companyShouldReceiveEstimatesOnEmail: false,
      allLocations: true,
      locationIds: [],
      limitations: [],
      subscriptionServices: [],
    },
    validate: {
      locationIds: (value, values) =>
        !values.allLocations && value.length === 0
          ? t`Izberite vsaj eno lokacijo`
          : null,
      price: (value) => (value < 0 ? t`Cena ne sme biti negativna.` : null),
      taxPercentage: (value) =>
        value != null && (value < 0 || value > 100)
          ? t`Davčna stopnja mora biti med 0 in 100.`
          : null,
      payerCompanyId: (_value, values) =>
        getCompanySplitFormErrors(values).payerCompanyId ?? null,
      companyShareValue: (_value, values) =>
        getCompanySplitFormErrors(values).companyShareValue ?? null,
      limitations: (value) =>
        getPlanValidationMessage(validatePlanLimitations(value)),
      subscriptionServices: (value) =>
        getPlanValidationMessage(validatePlanServices(value)),
    },
  });

  const planLimitations = isCustomSubscription
    ? form.values.limitations
    : (subscription?.limitations ?? []);
  const hasAllTimeframeLimit = planLimitations.some(
    (limitation) => limitation.timeframe === "all",
  );

  const hasPlanChanges =
    isCustomSubscription &&
    (form.isDirty("limitations") || form.isDirty("subscriptionServices"));
  // Unchanged locations are not sent, so a list the subscription has since narrowed stays valid.
  const hasLocationChanges =
    form.isDirty("allLocations") || form.isDirty("locationIds");

  const selectableServices = getSelectableServices(
    servicesData?.services ?? [],
    subscription?.customerSubscriptionServices ?? [],
  );

  const { isCoFunded, isCompanyFundingEverything } = getCompanySplitState(
    form.values,
  );

  useEffect(() => {
    if (!isOpen || !subscription) return;
    const initialFormValues: SettingsFormValues = {
      name: subscription.name ?? "",
      isRecurring: subscription.isRecurring,
      autoRenewOnVisitExhaustion:
        subscription.isRecurring && subscription.autoRenewOnVisitExhaustion,
      price: subscription.priceCents / 100,
      taxPercentage: subscription.taxPercentage,
      shouldIssueEstimates: subscription.shouldIssueEstimates,
      shouldReceiveEstimatesOnEmail: subscription.shouldReceiveEstimatesOnEmail,
      estimateOrganizationId: subscription.estimateOrganizationId ?? null,
      companySplitEnabled: subscription.payerCompanyId != null,
      payerCompanyId: subscription.payerCompanyId,
      companyShareType: subscription.companyShareType ?? "percentage",
      companyShareValue: (() => {
        if (subscription.companyShareValue == null) return 0;
        return subscription.companyShareType === "fixed"
          ? subscription.companyShareValue / 100
          : subscription.companyShareValue;
      })(),
      companyShouldReceiveEstimatesOnEmail:
        subscription.companyShouldReceiveEstimatesOnEmail,
      allLocations: subscription.allLocations,
      locationIds: subscription.locationIds,
      limitations: toPlanLimitations(subscription.limitations),
      subscriptionServices: subscription.customerSubscriptionServices.map(
        (service) => ({
          serviceId: service.serviceId,
          limitations: toPlanLimitations(service.limitations),
          limitEntries: service.limitations.length > 0,
        }),
      ),
    };
    form.setValues(initialFormValues);
    form.resetDirty(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscription?.id]);

  const selectedOrganizationTaxSystem = fiscalizations?.fiscalizations.find(
    (fiscalization) =>
      fiscalization.organizationId === form.values.estimateOrganizationId,
  )?.taxSystem;
  const taxSystem =
    selectedOrganizationTaxSystem ?? availableTaxSystems[0] ?? "furs";
  const taxPercentageItems = getTaxPercentageItems(taxSystem);

  useEffect(() => {
    if (!isOpen || !subscription || !isCustomSubscription) return;
    const availableValues = getTaxPercentageValues(taxSystem);
    if (
      form.values.taxPercentage != null &&
      availableValues.includes(form.values.taxPercentage)
    )
      return;

    form.setFieldValue("taxPercentage", getDefaultTaxPercentage(taxSystem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, taxSystem, subscription?.id]);

  const hasSplitChanges =
    form.isDirty("companySplitEnabled") ||
    form.isDirty("payerCompanyId") ||
    form.isDirty("companyShareType") ||
    form.isDirty("companyShareValue");

  const hasPricingChanges =
    form.isDirty("price") ||
    (hasActiveFiscalizations &&
      isCustomSubscription &&
      form.isDirty("taxPercentage")) ||
    (canIssueEstimates && form.isDirty("estimateOrganizationId"));

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const saveSettings = async (values: SettingsFormValues) => {
    const body = buildSettingsUpdateBody(values, {
      isCustomSubscription,
      hasAllTimeframeLimit,
      hasLocationChanges,
      hasPlanChanges,
      hasActiveFiscalizations,
      canIssueEstimates,
      offersBillingNotifications,
    });

    try {
      await updateSubscription({ customerSubscriptionId, body });
      addToast({
        title: t`Paket posodobljen`,
        description: t`Paket je bil uspešno posodobljen.`,
        color: "success",
      });
      handleClose();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ??
          t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    } finally {
      setPendingConfirmation(null);
    }
  };

  // The commitment is a promise to the customer, so the operator is told before a change to it
  // is written rather than after.
  const handleSubmit = (values: SettingsFormValues) => {
    if (isCommitmentLocked) {
      setPendingConfirmation(values);
      return;
    }
    void saveSettings(values);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="2xl"
        scrollBehavior="inside"
        data-identifier="settings-modal"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <ModalContent>
            <ModalHeader>
              <div className="flex flex-col justify-between">
                <Trans>Nastavitve paketa</Trans>
                <p className="text-xs font-medium">
                  {subscription?.variation?.name ?? ""}
                </p>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  {isCustomSubscription && (
                    <LimeInput
                      data-identifier="custom-subscription-name-input"
                      label={t`Ime paketa`}
                      labelPlacement="outside"
                      placeholder={t`Npr. Masaža 10×`}
                      maxLength={120}
                      {...form.getInputProps("name")}
                    />
                  )}
                  <LimeSwitch
                    data-identifier="settings-is-recurring-switch"
                    isCompact
                    size="sm"
                    isSelected={form.values.isRecurring}
                    onValueChange={(isRecurring) => {
                      form.setFieldValue("isRecurring", isRecurring);
                      if (!isRecurring) {
                        form.setFieldValue("autoRenewOnVisitExhaustion", false);
                      }
                    }}
                    title={t`Ponavljajoči`}
                    description={t`Paket se bo samodejno podaljševal na koncu vsakega obdobja, dokler ga ne prekličete.`}
                  />
                  {hasAllTimeframeLimit && form.values.isRecurring && (
                    <LimeSwitch
                      isCompact
                      size="sm"
                      {...form.getInputProps("autoRenewOnVisitExhaustion", {
                        type: "checkbox",
                      })}
                      title={t`Samodejno podaljšanje ob porabi obiskov`}
                      description={t`Paket se bo samodejno podaljšal, ko bodo obiski izčrpani.`}
                    />
                  )}
                </div>

                <CustomerSubscriptionLocationsField
                  form={form}
                  locations={offeredLocations}
                  isLoading={isLocationsLoading}
                />

                {isCustomSubscription && (
                  <>
                    <Divider />
                    <div
                      data-identifier="custom-subscription-plan-section"
                      className="flex flex-col gap-3"
                    >
                      <p className="text-medium font-semibold">
                        <Trans>Omejitve paketa</Trans>
                      </p>
                      <LimitationsSection
                        parentIndex={0}
                        limitations={form.values.limitations}
                        onAddLimitation={() =>
                          form.setFieldValue("limitations", [
                            ...form.values.limitations,
                            { timeframe: "all", limitAmount: 1 },
                          ])
                        }
                        onRemoveLimitation={(_, limitationIndex) =>
                          form.setFieldValue(
                            "limitations",
                            form.values.limitations.filter(
                              (_limitation, index) => index !== limitationIndex,
                            ),
                          )
                        }
                        onUpdateLimitation={(
                          _,
                          limitationIndex,
                          field,
                          value,
                        ) =>
                          form.setFieldValue(
                            `limitations.${limitationIndex}.${field}`,
                            value,
                          )
                        }
                      />
                      {form.errors.limitations && (
                        <p className="text-small text-danger">
                          {form.errors.limitations}
                        </p>
                      )}
                      <CustomSubscriptionServicesEditor
                        heading={
                          <p className="text-medium font-semibold">
                            <Trans>Storitve</Trans>
                          </p>
                        }
                        services={form.values.subscriptionServices}
                        onChange={(services) =>
                          form.setFieldValue("subscriptionServices", services)
                        }
                        availableServices={selectableServices}
                        isLoading={isServicesLoading}
                      />
                      {form.errors.subscriptionServices && (
                        <p className="text-small text-danger">
                          {form.errors.subscriptionServices}
                        </p>
                      )}
                    </div>
                  </>
                )}

                <Divider />

                <div className="flex flex-col gap-3">
                  <p className="text-medium font-semibold">
                    <Trans>Cena</Trans>
                  </p>
                  {hasAffectedIssuedEstimates && hasPricingChanges && (
                    <LimeAlert
                      color="warning"
                      title={t`Nekatera prihodnja obdobja imajo že izdane predračune`}
                      description={
                        canIssueEstimates
                          ? t`Sprememba cene ne bo posodobila prihodnjih obdobij (${affectedIssuedEstimatesCount}) z že izdanim predračunom. Sprememba davčne stopnje velja za nove predračune in ne posodobi že izdanih dokumentov.`
                          : t`Sprememba cene ne bo posodobila prihodnjih obdobij (${affectedIssuedEstimatesCount}) z že izdanim predračunom.`
                      }
                    />
                  )}
                  {isCommitmentLocked && (
                    <LimeAlert
                      data-identifier="commitment-locked-alert"
                      color="primary"
                      title={t`Paket ima vezavo`}
                      description={t`Spremembe cene, sofinanciranja in ponavljanja vplivajo na sklenjeno vezavo.`}
                    />
                  )}
                  <LimeNumberInput
                    data-identifier="pricing-price-input"
                    label={t`Cena`}
                    labelPlacement="outside"
                    minValue={0}
                    step={0.01}
                    endContent={currencySymbol}
                    {...form.getInputProps("price")}
                  />
                  {hasActiveFiscalizations && isCustomSubscription && (
                    <LimeSelect
                      data-identifier="pricing-tax-percentage-select"
                      label={t`Davčna stopnja`}
                      labelPlacement="outside"
                      value={
                        form.values.taxPercentage == null
                          ? ""
                          : String(form.values.taxPercentage)
                      }
                      onSingleItemChange={(value) =>
                        form.setFieldValue("taxPercentage", parseFloat(value))
                      }
                      items={taxPercentageItems}
                    />
                  )}
                </div>

                <Divider />
                <div className="flex flex-col gap-2">
                  <p className="text-medium font-semibold">
                    <Trans>Sofinanciranje</Trans>
                  </p>
                  {hasAffectedIssuedEstimates && hasSplitChanges && (
                    <LimeAlert
                      data-identifier="company-split-issued-estimates-warning"
                      color="warning"
                      title={t`Nekatera prihodnja obdobja imajo že izdane predračune`}
                      description={t`Sprememba sofinanciranja ne bo posodobila prihodnjih obdobij (${affectedIssuedEstimatesCount}) z že izdanim predračunom. Ko obdobju odstranite izdane predračune, se razdeli po novem sofinanciranju.`}
                    />
                  )}
                  <CompanySplitFields
                    issuesEstimates={
                      canIssueEstimates && form.values.shouldIssueEstimates
                    }
                    isEnabled={form.values.companySplitEnabled}
                    onEnabledChange={(isEnabled) =>
                      form.setFieldValue("companySplitEnabled", isEnabled)
                    }
                    payerCompanyId={form.values.payerCompanyId}
                    onPayerCompanyIdChange={(payerCompanyId) =>
                      form.setFieldValue("payerCompanyId", payerCompanyId)
                    }
                    shareType={form.values.companyShareType}
                    onShareTypeChange={(shareType) =>
                      form.setFieldValue("companyShareType", shareType)
                    }
                    shareValue={form.values.companyShareValue}
                    onShareValueChange={(shareValue) =>
                      form.setFieldValue("companyShareValue", shareValue)
                    }
                    currencySymbol={currencySymbol}
                    companyReceivesEstimates={
                      form.values.companyShouldReceiveEstimatesOnEmail
                    }
                    onCompanyReceivesEstimatesChange={(
                      companyReceivesEstimates,
                    ) =>
                      form.setFieldValue(
                        "companyShouldReceiveEstimatesOnEmail",
                        companyReceivesEstimates,
                      )
                    }
                    companyFundsEverything={isCompanyFundingEverything}
                    companyError={form.errors.payerCompanyId}
                    valueError={form.errors.companyShareValue}
                  />
                </div>

                {canIssueEstimates && (
                  <>
                    <Divider />
                    <div className="flex flex-col gap-2">
                      <p className="text-medium font-semibold">
                        <Trans>Predračuni</Trans>
                      </p>
                      <LimeSwitch
                        size="sm"
                        {...form.getInputProps("shouldIssueEstimates", {
                          type: "checkbox",
                        })}
                        title={t`Izdajaj predračune`}
                        isCompact={true}
                        description={t`Če je izklopljeno, se predračuni za ta paket ne bodo izdajali (tudi ne samodejno).`}
                      />
                      <LimeSelect
                        data-identifier="pricing-estimate-organization-select"
                        label={t`Organizacija za predračune`}
                        labelPlacement="inside"
                        placeholder={defaultEstimateOrganizationPlaceholder}
                        description={t`Če ni izbrana, se uporabi privzeta organizacija iz nastavitev paketov.`}
                        selectionMode="single"
                        disallowEmptySelection={false}
                        isDisabled={!form.values.shouldIssueEstimates}
                        items={(fiscalizations?.fiscalizations ?? []).map(
                          (fiscalization) => ({
                            key: fiscalization.organizationId,
                            label: `${fiscalization.organizationName!} (${fiscalization.taxSystem.toUpperCase()})`,
                          }),
                        )}
                        {...form.getInputProps("estimateOrganizationId")}
                      />
                    </div>
                  </>
                )}

                {offersBillingNotifications && (
                  <>
                    <Divider />
                    <div className="flex flex-col gap-2">
                      <p className="text-medium font-semibold">
                        <Trans>Obvestila</Trans>
                      </p>
                      <LimeSwitch
                        size="sm"
                        {...form.getInputProps(
                          "shouldReceiveEstimatesOnEmail",
                          {
                            type: "checkbox",
                          },
                        )}
                        isCompact
                        {...billingNotificationCopy({
                          hasSubscriptionNotifications,
                          isCoFunded,
                        })}
                      />
                    </div>
                  </>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={handleClose}
                isDisabled={isUpdatingSubscription}
              >
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                data-identifier="settings-submit-btn"
                color="primary"
                variant="solid"
                type="submit"
                isLoading={isUpdatingSubscription || isSubscriptionLoading}
              >
                <Trans>Potrdi spremembe</Trans>
              </Button>
            </ModalFooter>
          </ModalContent>
        </form>
      </Modal>
      <CommitmentWarningDialog
        isOpen={pendingConfirmation != null}
        isSaving={isUpdatingSubscription}
        onDismiss={() => setPendingConfirmation(null)}
        onConfirm={() => {
          if (pendingConfirmation) {
            void saveSettings(pendingConfirmation);
          }
        }}
      />
    </>
  );
};

// Without subscription notifications the switch only gates the emailed proforma.
function billingNotificationCopy({
  hasSubscriptionNotifications,
  isCoFunded,
}: {
  hasSubscriptionNotifications: boolean;
  isCoFunded: boolean;
}): { title: string; description: string } {
  if (!hasSubscriptionNotifications) {
    return {
      title: t`Prejemanje predračunov na e-pošto`,
      description: isCoFunded
        ? t`Stranka bo prejemala predračune za svoj del pred iztekom paketa na e-pošto. Podjetje svoj račun prejme ločeno.`
        : t`Stranka bo prejemala predračune pred iztekom paketa na e-pošto.`,
    };
  }

  return {
    title: t`Pošiljaj obvestila o plačilih`,
    description: isCoFunded
      ? t`Stranka prejema obvestila o plačilih za svoj del paketa (predračuni, opomniki, opozorila) po vseh kanalih. Podjetje svoj račun prejme ločeno.`
      : t`Stranka prejema obvestila o plačilih paketa (predračuni, opomniki, opozorila) po vseh kanalih.`,
  };
}
